import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GlobalAttributesService,
  Location,
  ComplianceStatus,
  HealthStatus,
  AssetState,
  Criticality,
  Category,
  Department,
  CreateLocationDto,
  UpdateLocationDto,
  CreateComplianceStatusDto,
  UpdateComplianceStatusDto,
  CreateHealthStatusDto,
  UpdateHealthStatusDto,
  CreateAssetStateDto,
  UpdateAssetStateDto,
  CreateCriticalityDto,
  UpdateCriticalityDto,
  CreateCategoryDto,
  UpdateCategoryDto,
  CreateDepartmentDto,
  UpdateDepartmentDto,
  PaginationParams,
  AxiosErrorResponse,
} from "@/lib/api/services/globalAttributesService";

export interface GlobalAttributesState {
  locations: Location[];
  locationsLoading: boolean;
  locationsError: string | null;
  locationsSearch: string;
  locationsPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  complianceStatuses: ComplianceStatus[];
  complianceStatusesLoading: boolean;
  complianceStatusesError: string | null;
  complianceStatusesSearch: string;
  complianceStatusesPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  healthStatuses: HealthStatus[];
  healthStatusesLoading: boolean;
  healthStatusesError: string | null;
  healthStatusesSearch: string;
  healthStatusesPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  assetStates: AssetState[];
  assetStatesLoading: boolean;
  assetStatesError: string | null;
  assetStatesSearch: string;
  assetStatesPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  criticalities: Criticality[];
  criticalitiesLoading: boolean;
  criticalitiesError: string | null;
  criticalitiesSearch: string;
  criticalitiesPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  categories: Category[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  categoriesSearch: string;
  categoriesPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  departments: Department[];
  departmentsLoading: boolean;
  departmentsError: string | null;
  departmentsSearch: string;
  departmentsPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;

  allAttributesLoading: boolean;
  allAttributesError: string | null;

  selectedOrganizationId: number | null;
  selectedSiteId: number | null;
}

const initialState: GlobalAttributesState = {
  locations: [],
  locationsLoading: false,
  locationsError: null,
  locationsSearch: "",
  locationsPagination: null,

  complianceStatuses: [],
  complianceStatusesLoading: false,
  complianceStatusesError: null,
  complianceStatusesSearch: "",
  complianceStatusesPagination: null,

  healthStatuses: [],
  healthStatusesLoading: false,
  healthStatusesError: null,
  healthStatusesSearch: "",
  healthStatusesPagination: null,

  assetStates: [],
  assetStatesLoading: false,
  assetStatesError: null,
  assetStatesSearch: "",
  assetStatesPagination: null,

  criticalities: [],
  criticalitiesLoading: false,
  criticalitiesError: null,
  criticalitiesSearch: "",
  criticalitiesPagination: null,

  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  categoriesSearch: "",
  categoriesPagination: null,

  departments: [],
  departmentsLoading: false,
  departmentsError: null,
  departmentsSearch: "",
  departmentsPagination: null,

  allAttributesLoading: false,
  allAttributesError: null,

  selectedOrganizationId: null,
  selectedSiteId: null,
};

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export const getAllAttributes = createAsyncThunk(
  "globalAttributes/getAllAttributes",
  async (
    {
      organizationId,
      siteId,
      search,
    }: { organizationId: number; siteId: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getAllAttributes(
        organizationId,
        siteId,
        { search },
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getLocations = createAsyncThunk(
  "globalAttributes/getLocations",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getLocations(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createLocation = createAsyncThunk(
  "globalAttributes/createLocation",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateLocationDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createLocation(organizationId, data);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateLocation = createAsyncThunk(
  "globalAttributes/updateLocation",
  async (
    {
      organizationId,
      siteId,
      locationId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      locationId: number;
      data: UpdateLocationDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateLocation(
        organizationId,
        siteId,
        locationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteLocation = createAsyncThunk(
  "globalAttributes/deleteLocation",
  async (
    {
      organizationId,
      siteId,
      locationId,
    }: { organizationId: number; siteId: number; locationId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteLocation(
        organizationId,
        siteId,
        locationId,
      );
      return locationId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getComplianceStatuses = createAsyncThunk(
  "globalAttributes/getComplianceStatuses",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getComplianceStatuses(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createComplianceStatus = createAsyncThunk(
  "globalAttributes/createComplianceStatus",
  async (
    {
      organizationId,
      data,
    }: {
      organizationId: number;
      data: CreateComplianceStatusDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createComplianceStatus(
        organizationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateComplianceStatus = createAsyncThunk(
  "globalAttributes/updateComplianceStatus",
  async (
    {
      organizationId,
      siteId,
      statusId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      statusId: number;
      data: UpdateComplianceStatusDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateComplianceStatus(
        organizationId,
        siteId,
        statusId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteComplianceStatus = createAsyncThunk(
  "globalAttributes/deleteComplianceStatus",
  async (
    {
      organizationId,
      siteId,
      statusId,
    }: { organizationId: number; siteId: number; statusId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteComplianceStatus(
        organizationId,
        siteId,
        statusId,
      );
      return statusId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getHealthStatuses = createAsyncThunk(
  "globalAttributes/getHealthStatuses",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getHealthStatuses(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createHealthStatus = createAsyncThunk(
  "globalAttributes/createHealthStatus",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateHealthStatusDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createHealthStatus(
        organizationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateHealthStatus = createAsyncThunk(
  "globalAttributes/updateHealthStatus",
  async (
    {
      organizationId,
      siteId,
      healthId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      healthId: number;
      data: UpdateHealthStatusDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateHealthStatus(
        organizationId,
        siteId,
        healthId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteHealthStatus = createAsyncThunk(
  "globalAttributes/deleteHealthStatus",
  async (
    {
      organizationId,
      siteId,
      healthId,
    }: { organizationId: number; siteId: number; healthId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteHealthStatus(
        organizationId,
        siteId,
        healthId,
      );
      return healthId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetStates = createAsyncThunk(
  "globalAttributes/getAssetStates",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getAssetStates(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createAssetState = createAsyncThunk(
  "globalAttributes/createAssetState",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateAssetStateDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createAssetState(
        organizationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAssetState = createAsyncThunk(
  "globalAttributes/updateAssetState",
  async (
    {
      organizationId,
      siteId,
      stateId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      stateId: number;
      data: UpdateAssetStateDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateAssetState(
        organizationId,
        siteId,
        stateId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteAssetState = createAsyncThunk(
  "globalAttributes/deleteAssetState",
  async (
    {
      organizationId,
      siteId,
      stateId,
    }: { organizationId: number; siteId: number; stateId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteAssetState(
        organizationId,
        siteId,
        stateId,
      );
      return stateId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getCriticalities = createAsyncThunk(
  "globalAttributes/getCriticalities",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getCriticalities(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createCriticality = createAsyncThunk(
  "globalAttributes/createCriticality",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateCriticalityDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createCriticality(
        organizationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateCriticality = createAsyncThunk(
  "globalAttributes/updateCriticality",
  async (
    {
      organizationId,
      siteId,
      criticalityId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      criticalityId: number;
      data: UpdateCriticalityDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateCriticality(
        organizationId,
        siteId,
        criticalityId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteCriticality = createAsyncThunk(
  "globalAttributes/deleteCriticality",
  async (
    {
      organizationId,
      siteId,
      criticalityId,
    }: { organizationId: number; siteId: number; criticalityId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteCriticality(
        organizationId,
        siteId,
        criticalityId,
      );
      return criticalityId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getCategories = createAsyncThunk(
  "globalAttributes/getCategories",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getCategories(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createCategory = createAsyncThunk(
  "globalAttributes/createCategory",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateCategoryDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createCategory(organizationId, data);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateCategory = createAsyncThunk(
  "globalAttributes/updateCategory",
  async (
    {
      organizationId,
      siteId,
      categoryId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      categoryId: number;
      data: UpdateCategoryDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateCategory(
        organizationId,
        siteId,
        categoryId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "globalAttributes/deleteCategory",
  async (
    {
      organizationId,
      siteId,
      categoryId,
    }: { organizationId: number; siteId: number; categoryId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteCategory(
        organizationId,
        siteId,
        categoryId,
      );
      return categoryId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getDepartments = createAsyncThunk(
  "globalAttributes/getDepartments",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.getDepartments(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createDepartment = createAsyncThunk(
  "globalAttributes/createDepartment",
  async (
    {
      organizationId,
      data,
    }: { organizationId: number; data: CreateDepartmentDto },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.createDepartment(
        organizationId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateDepartment = createAsyncThunk(
  "globalAttributes/updateDepartment",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      data,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      data: UpdateDepartmentDto;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.updateDepartment(
        organizationId,
        siteId,
        departmentId,
        data,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteDepartment = createAsyncThunk(
  "globalAttributes/deleteDepartment",
  async (
    {
      organizationId,
      siteId,
      departmentId,
    }: { organizationId: number; siteId: number; departmentId: number },
    { rejectWithValue },
  ) => {
    try {
      await GlobalAttributesService.deleteDepartment(
        organizationId,
        siteId,
        departmentId,
      );
      return departmentId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const toggleAssetStateVisibility = createAsyncThunk(
  "globalAttributes/toggleAssetStateVisibility",
  async (
    {
      organizationId,
      siteId,
      stateId,
      isHidden,
    }: {
      organizationId: number;
      siteId: number;
      stateId: number;
      isHidden: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      return await GlobalAttributesService.toggleAssetStateVisibility(
        organizationId,
        siteId,
        stateId,
        isHidden,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const globalAttributesSlice = createSlice({
  name: "globalAttributes",
  initialState,
  reducers: {
    setSelectedOrganizationAndSite: (
      state,
      action: PayloadAction<{ organizationId: number; siteId: number }>,
    ) => {
      state.selectedOrganizationId = action.payload.organizationId;
      state.selectedSiteId = action.payload.siteId;
    },
    setLocationsSearch: (state, action: PayloadAction<string>) => {
      state.locationsSearch = action.payload;
    },
    setComplianceStatusesSearch: (state, action: PayloadAction<string>) => {
      state.complianceStatusesSearch = action.payload;
    },
    setHealthStatusesSearch: (state, action: PayloadAction<string>) => {
      state.healthStatusesSearch = action.payload;
    },
    setAssetStatesSearch: (state, action: PayloadAction<string>) => {
      state.assetStatesSearch = action.payload;
    },
    setCriticalitiesSearch: (state, action: PayloadAction<string>) => {
      state.criticalitiesSearch = action.payload;
    },
    setCategoriesSearch: (state, action: PayloadAction<string>) => {
      state.categoriesSearch = action.payload;
    },
    setDepartmentsSearch: (state, action: PayloadAction<string>) => {
      state.departmentsSearch = action.payload;
    },
    clearLocationsError: (state) => {
      state.locationsError = null;
    },
    clearComplianceStatusesError: (state) => {
      state.complianceStatusesError = null;
    },
    clearHealthStatusesError: (state) => {
      state.healthStatusesError = null;
    },
    clearAssetStatesError: (state) => {
      state.assetStatesError = null;
    },
    clearCriticalitiesError: (state) => {
      state.criticalitiesError = null;
    },
    clearCategoriesError: (state) => {
      state.categoriesError = null;
    },
    clearDepartmentsError: (state) => {
      state.departmentsError = null;
    },
    clearAllAttributesError: (state) => {
      state.allAttributesError = null;
    },
    resetGlobalAttributes: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAttributes.pending, (state) => {
        state.allAttributesLoading = true;
        state.allAttributesError = null;
      })
      .addCase(getAllAttributes.fulfilled, (state, action) => {
        state.allAttributesLoading = false;
        state.locations = action.payload.data.locations;
        state.complianceStatuses = action.payload.data.complianceStatuses;
        state.healthStatuses = action.payload.data.healthStatuses;
        state.assetStates = action.payload.data.assetStates;
        state.criticalities = action.payload.data.criticalities;
        state.categories = action.payload.data.categories;
        state.departments = action.payload.data.departments;
      })
      .addCase(getAllAttributes.rejected, (state, action) => {
        state.allAttributesLoading = false;
        state.allAttributesError = action.payload as string;
      })

      .addCase(getLocations.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(getLocations.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.locations = action.payload.data;
        state.locationsPagination = action.payload.pagination || null;
      })
      .addCase(getLocations.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })
      .addCase(createLocation.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(createLocation.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.locations.unshift(...action.payload.locations);
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })
      .addCase(updateLocation.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(updateLocation.fulfilled, (state, action) => {
        state.locationsLoading = false;
        const updatedLocation = action.payload.location;
        const index = state.locations.findIndex(
          (l) => l.id === updatedLocation.id,
        );
        if (index !== -1) {
          state.locations[index] = updatedLocation;
        }
      })
      .addCase(updateLocation.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })
      .addCase(deleteLocation.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.locations = state.locations.filter(
          (l) => l.id !== action.payload,
        );
      })
      .addCase(deleteLocation.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })

      .addCase(getComplianceStatuses.pending, (state) => {
        state.complianceStatusesLoading = true;
        state.complianceStatusesError = null;
      })
      .addCase(getComplianceStatuses.fulfilled, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatuses = action.payload.data;
        state.complianceStatusesPagination = action.payload.pagination || null;
      })
      .addCase(getComplianceStatuses.rejected, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatusesError = action.payload as string;
      })
      .addCase(createComplianceStatus.pending, (state) => {
        state.complianceStatusesLoading = true;
        state.complianceStatusesError = null;
      })
      .addCase(createComplianceStatus.fulfilled, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatuses.unshift(...action.payload.complianceStatuses);
      })
      .addCase(createComplianceStatus.rejected, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatusesError = action.payload as string;
      })
      .addCase(updateComplianceStatus.pending, (state) => {
        state.complianceStatusesLoading = true;
        state.complianceStatusesError = null;
      })
      .addCase(updateComplianceStatus.fulfilled, (state, action) => {
        state.complianceStatusesLoading = false;
        const updatedStatus = action.payload.complianceStatus;
        const index = state.complianceStatuses.findIndex(
          (cs) => cs.id === updatedStatus.id,
        );
        if (index !== -1) {
          state.complianceStatuses[index] = updatedStatus;
        }
      })
      .addCase(updateComplianceStatus.rejected, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatusesError = action.payload as string;
      })
      .addCase(deleteComplianceStatus.pending, (state) => {
        state.complianceStatusesLoading = true;
        state.complianceStatusesError = null;
      })
      .addCase(deleteComplianceStatus.fulfilled, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatuses = state.complianceStatuses.filter(
          (cs) => cs.id !== action.payload,
        );
      })
      .addCase(deleteComplianceStatus.rejected, (state, action) => {
        state.complianceStatusesLoading = false;
        state.complianceStatusesError = action.payload as string;
      })

      .addCase(getHealthStatuses.pending, (state) => {
        state.healthStatusesLoading = true;
        state.healthStatusesError = null;
      })
      .addCase(getHealthStatuses.fulfilled, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatuses = action.payload.data;
        state.healthStatusesPagination = action.payload.pagination || null;
      })
      .addCase(getHealthStatuses.rejected, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatusesError = action.payload as string;
      })
      .addCase(createHealthStatus.pending, (state) => {
        state.healthStatusesLoading = true;
        state.healthStatusesError = null;
      })
      .addCase(createHealthStatus.fulfilled, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatuses.unshift(...action.payload.healthStatuses);
      })
      .addCase(createHealthStatus.rejected, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatusesError = action.payload as string;
      })
      .addCase(updateHealthStatus.pending, (state) => {
        state.healthStatusesLoading = true;
        state.healthStatusesError = null;
      })
      .addCase(updateHealthStatus.fulfilled, (state, action) => {
        state.healthStatusesLoading = false;
        const updatedHealth = action.payload.healthStatus;
        const index = state.healthStatuses.findIndex(
          (ah) => ah.id === updatedHealth.id,
        );
        if (index !== -1) {
          state.healthStatuses[index] = updatedHealth;
        }
      })
      .addCase(updateHealthStatus.rejected, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatusesError = action.payload as string;
      })
      .addCase(deleteHealthStatus.pending, (state) => {
        state.healthStatusesLoading = true;
        state.healthStatusesError = null;
      })
      .addCase(deleteHealthStatus.fulfilled, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatuses = state.healthStatuses.filter(
          (ah) => ah.id !== action.payload,
        );
      })
      .addCase(deleteHealthStatus.rejected, (state, action) => {
        state.healthStatusesLoading = false;
        state.healthStatusesError = action.payload as string;
      })

      .addCase(getAssetStates.pending, (state) => {
        state.assetStatesLoading = true;
        state.assetStatesError = null;
      })
      .addCase(getAssetStates.fulfilled, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStates = action.payload.data;
        state.assetStatesPagination = action.payload.pagination || null;
      })
      .addCase(getAssetStates.rejected, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStatesError = action.payload as string;
      })
      .addCase(createAssetState.pending, (state) => {
        state.assetStatesLoading = true;
        state.assetStatesError = null;
      })
      .addCase(createAssetState.fulfilled, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStates.unshift(...action.payload.assetStates);
      })
      .addCase(createAssetState.rejected, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStatesError = action.payload as string;
      })
      .addCase(updateAssetState.pending, (state) => {
        state.assetStatesLoading = true;
        state.assetStatesError = null;
      })
      .addCase(updateAssetState.fulfilled, (state, action) => {
        state.assetStatesLoading = false;
        const updatedState = action.payload.assetState;
        const index = state.assetStates.findIndex(
          (as) => as.id === updatedState.id,
        );
        if (index !== -1) {
          state.assetStates[index] = updatedState;
        }
      })
      .addCase(updateAssetState.rejected, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStatesError = action.payload as string;
      })
      .addCase(deleteAssetState.pending, (state) => {
        state.assetStatesLoading = true;
        state.assetStatesError = null;
      })
      .addCase(deleteAssetState.fulfilled, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStates = state.assetStates.filter(
          (as) => as.id !== action.payload,
        );
      })
      .addCase(deleteAssetState.rejected, (state, action) => {
        state.assetStatesLoading = false;
        state.assetStatesError = action.payload as string;
      })

      .addCase(getCriticalities.pending, (state) => {
        state.criticalitiesLoading = true;
        state.criticalitiesError = null;
      })
      .addCase(getCriticalities.fulfilled, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalities = action.payload.data;
        state.criticalitiesPagination = action.payload.pagination || null;
      })
      .addCase(getCriticalities.rejected, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalitiesError = action.payload as string;
      })
      .addCase(createCriticality.pending, (state) => {
        state.criticalitiesLoading = true;
        state.criticalitiesError = null;
      })
      .addCase(createCriticality.fulfilled, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalities.unshift(...action.payload.criticalities);
      })
      .addCase(createCriticality.rejected, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalitiesError = action.payload as string;
      })
      .addCase(updateCriticality.pending, (state) => {
        state.criticalitiesLoading = true;
        state.criticalitiesError = null;
      })
      .addCase(updateCriticality.fulfilled, (state, action) => {
        state.criticalitiesLoading = false;
        const updatedCriticality = action.payload.criticality;
        const index = state.criticalities.findIndex(
          (c) => c.id === updatedCriticality.id,
        );
        if (index !== -1) {
          state.criticalities[index] = updatedCriticality;
        }
      })
      .addCase(updateCriticality.rejected, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalitiesError = action.payload as string;
      })
      .addCase(deleteCriticality.pending, (state) => {
        state.criticalitiesLoading = true;
        state.criticalitiesError = null;
      })
      .addCase(deleteCriticality.fulfilled, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalities = state.criticalities.filter(
          (c) => c.id !== action.payload,
        );
      })
      .addCase(deleteCriticality.rejected, (state, action) => {
        state.criticalitiesLoading = false;
        state.criticalitiesError = action.payload as string;
      })

      .addCase(getCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload.data;
        state.categoriesPagination = action.payload.pagination || null;
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })
      .addCase(createCategory.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories.unshift(...action.payload.categories);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })
      .addCase(updateCategory.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        const updatedCategory = action.payload.category;
        const index = state.categories.findIndex(
          (cat) => cat.id === updatedCategory.id,
        );
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })
      .addCase(deleteCategory.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = state.categories.filter(
          (cat) => cat.id !== action.payload,
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })

      .addCase(getDepartments.pending, (state) => {
        state.departmentsLoading = true;
        state.departmentsError = null;
      })
      .addCase(getDepartments.fulfilled, (state, action) => {
        state.departmentsLoading = false;
        state.departments = action.payload.data;
        state.departmentsPagination = action.payload.pagination || null;
      })
      .addCase(getDepartments.rejected, (state, action) => {
        state.departmentsLoading = false;
        state.departmentsError = action.payload as string;
      })
      .addCase(createDepartment.pending, (state) => {
        state.departmentsLoading = true;
        state.departmentsError = null;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.departmentsLoading = false;
        state.departments.unshift(...action.payload.departments);
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.departmentsLoading = false;
        state.departmentsError = action.payload as string;
      })
      .addCase(updateDepartment.pending, (state) => {
        state.departmentsLoading = true;
        state.departmentsError = null;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.departmentsLoading = false;
        const updatedDepartment = action.payload.department;
        const index = state.departments.findIndex(
          (dept) => dept.id === updatedDepartment.id,
        );
        if (index !== -1) {
          state.departments[index] = updatedDepartment;
        }
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.departmentsLoading = false;
        state.departmentsError = action.payload as string;
      })
      .addCase(deleteDepartment.pending, (state) => {
        state.departmentsLoading = true;
        state.departmentsError = null;
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.departmentsLoading = false;
        state.departments = state.departments.filter(
          (dept) => dept.id !== action.payload,
        );
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.departmentsLoading = false;
        state.departmentsError = action.payload as string;
      })
      .addCase(toggleAssetStateVisibility.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.assetStates.findIndex((s) => s.id === updated.id);
        if (index !== -1) {
          state.assetStates[index] = updated;
        }
      });
  },
});

export const {
  setSelectedOrganizationAndSite,
  clearLocationsError,
  clearComplianceStatusesError,
  clearHealthStatusesError,
  clearAssetStatesError,
  clearCriticalitiesError,
  clearCategoriesError,
  clearDepartmentsError,
  clearAllAttributesError,
  resetGlobalAttributes,
} = globalAttributesSlice.actions;

export default globalAttributesSlice.reducer;
